import { FormatType } from '@revelio/iso-utility';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import { FilterId } from '../../filters/types/filters';
import {
  demandSupplyRatioRankInfoText,
  marketTightnessInfoText,
  timeToFillRankInfoText,
  wagePressureRankInfoText,
} from '../market-conditions-info-text';

const items: PositionedItem[] = [
  {
    id: '5037bcc3-67ac-498e-b420-6e27112d7589',
    title: 'Market Tightness',
    isTitleManuallySet: true,
    position: { x: 0, y: 0, width: 30, height: 12 },
    info: { text: marketTightnessInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "market_tightness"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'c4aad591-4f2d-47d0-b39f-e9b3dd1a00a4',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 12, width: 10, height: 11 },
    info: { text: demandSupplyRatioRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "demand_supply_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'e6a01887-1e2f-415d-ade9-cceb3975916a',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 12, width: 10, height: 11 },
    info: { text: wagePressureRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "wage_pressure_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '4d1d07dc-95d0-4eb9-901d-b45f7adb7bae',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 12, width: 10, height: 11 },
    info: { text: timeToFillRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "time_to_fill_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'fba4ed7d-8f54-4cf8-b5fc-b01283941509',
    title: 'Demand/Supply',
    position: { x: 0, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['demand_supply'],
      format: FormatType.RATIO,
    },
  },
  {
    id: '7e8d49ba-24d3-4c7b-afe7-1a879c6f086d',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['wage_pressure'],
      format: FormatType.PERCENTAGE,
    },
  },
  {
    id: '48bc53bb-a2f2-437d-ba47-3cc946baf782',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['time_to_fill'],
      format: FormatType.INTEGER,
    },
  },
  {
    id: '3ffe9d1f-e89e-40a2-9cbf-6a45f0461082',
    title: 'Postings by Company',
    isTitleManuallySet: true,
    position: { x: 30, y: 0, width: 18, height: 36 },
    plot: {
      chartType: 'grouped-bar',
      secondaryDimension: 'company',
      defaultMetricOptions: ['postings_active'],
      format: FormatType.DECIMAL,
    },
  },
];

const tabContent: TabContent = {
  id: RevelioPageId.ACTIVITY_MARKET_CONDITIONS,
  primaryFilter: { id: FilterId.ACTIVITY },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.INDUSTRY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  items,
};

export const activityMarketConditions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.ACTIVITY_MARKET_CONDITIONS,
  revelioPageId: RevelioPageId.ACTIVITY_MARKET_CONDITIONS,
  name: 'Market Conditions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: RevelioPageId.ACTIVITY_MARKET_CONDITIONS,
      label: 'Market Conditions',
      isActive: true,
      tabContent,
    },
  ],
};
